import { DateTime } from "luxon";
import { Link, PaginationPayload, useGetList } from "react-admin";
import { useAutovioContext } from "../../../hooks/useAutovioContext";
import { Avatar, Box, IconButton, Skeleton, Theme, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { formatDate } from "../../../utils/calendar";
import { Student } from "../../../providers/studentsProvider";
import { BookedTraining } from "../../../model/BookedTraining";
import { useNavigate } from "react-router-dom";
import { DashboardCard } from "../../../misc/DashboardCard";
import { grants } from "../../../backoffice.access_control";
import AddIcon from "@mui/icons-material/Add";
import { Column } from "../../../misc/Column";
import { Row } from "../../../misc/Row";
import { useEffect } from "react";

export function LatestDrivingStudentsCard() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const {
    data: students,
    isLoading,
    error,
  } = useGetList<Student>("students", {
    filter: {
      drivingSchoolId,
      __record__: (record: Student) => record.startDate && DateTime.now().diff(record.startDate, "days").days < 30,
    },
    pagination: { page: 1, perPage: 100 } as PaginationPayload,
    sort: { field: "startDate", order: "DESC" },
  });
  const visibleStudents = isMobile ? students?.slice(0, 10) : students;

  useEffect(() => {
    if (error) {
      handleError("Fehler beim Laden der neuesten Fahrschüler.", error);
    }
  }, [error]);

  function getBookedTrainingLabel(bookedTrainings: BookedTraining[]) {
    return bookedTrainings
      ?.map((it) => it.shortLabel)
      ?.sort()
      .join(", ");
  }

  return (
    <DashboardCard
      title="Neue Fahrschüler"
      sx={{
        height: "504px",
        display: "flex",
        flexDirection: "column",
        "& > .MuiBox-root:last-child": { flex: 1, minHeight: 0 },
      }}
      topRightCorner={
        <Row alignItems="center">
          <Link
            to={{ pathname: `/drivingSchools/${drivingSchoolId}/students`, search: studentsListQueryParams }}
            sx={{ fontSize: 14, padding: "8px" }}
          >
            Alle anzeigen
          </Link>
          {grants.has("Quote:create") && (
            <Tooltip title="Angebot erstellen">
              <IconButton
                aria-label="Angebot erstellen"
                onClick={() => {
                  const url = `https://autovio.de/lp/angebot-erstellen/?schoolid=${drivingSchoolId}`;
                  window.open(url, "_blank");
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Row>
      }
    >
      {isLoading ? (
        <_LatestDrivingStudentsSkeleton />
      ) : error ? (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 14, textAlign: "center" }}>
            Die neuesten Fahrschüler konnten nicht geladen werden.
          </Typography>
        </Box>
      ) : !students || students.length === 0 ? (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 14, textAlign: "center" }}>
            Keine neuen Fahrschüler in den letzten 30 Tagen.
          </Typography>
        </Box>
      ) : (
        <Column sx={{ gap: "10px", overflowY: { xs: "hidden", sm: "auto" }, maxHeight: { xs: "none", sm: "420px" } }}>
          {visibleStudents?.map((student) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                gap: 1.5,
                minWidth: 0,
              }}
              key={student.id}
              onClick={() => navigate(`/students/${student.id}`)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
                <Box>
                  <Avatar src={student?.avatarUrl} sx={{ width: 32, height: 32 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: "normal",
                        fontSize: 14,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: { xs: "min(46vw, 200px)", md: "200px" },
                      }}
                    >
                      {student?.firstName} {student?.lastName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: "normal",
                        fontSize: 11,
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: { xs: "min(46vw, 200px)", md: "200px" },
                      }}
                    >
                      {getBookedTrainingLabel(student.bookedTrainings)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {student.startDate && (
                <Box>
                  <Typography sx={{ fontWeight: "normal", fontSize: 14, color: "text.secondary" }}>
                    {formatDate(student.startDate, true)}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Column>
      )}
    </DashboardCard>
  );
}

function _LatestDrivingStudentsSkeleton() {
  return (
    <Column role="status" aria-label="Fahrschüler werden geladen" sx={{ gap: "10px" }}>
      {Array.from({ length: 9 }, (_value, index) => (
        <Row key={index} alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
          <Row alignItems="center" sx={{ flex: 1, gap: "14px", minWidth: 0 }}>
            <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width="65%" sx={{ fontSize: 14 }} />
              <Skeleton variant="text" width="40%" sx={{ fontSize: 11 }} />
            </Box>
          </Row>
          <Skeleton variant="text" width={72} sx={{ fontSize: 14 }} />
        </Row>
      ))}
    </Column>
  );
}

const studentsListQueryParams = new URLSearchParams({
  filter: JSON.stringify({}),
  order: "DESC",
  sort: "startDate",
}).toString();
